import { inject, Injectable, signal } from '@angular/core';
import { FIREBASE_AUTH, FIRESTORE } from '@context/frontend/common';
import { AuthUser } from '@context/shared/types/user';
import {
	User as FirebaseUser,
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signOut,
} from 'firebase/auth';
import {
	collection,
	CollectionReference,
	doc,
	getDoc,
} from 'firebase/firestore';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

/**
 * Auth-only service for the admin console. Handles Firebase auth and AuthUser
 * document only. No organization selection or org-specific user state—super admins
 * are considered to have access to all organizations.
 */
@Injectable({ providedIn: 'root' })
export class SuperAuthService {
	readonly firebaseUser = signal<FirebaseUser | null>(null);
	/** Highest role across all of the auth user's org memberships. */
	readonly authUser = signal<AuthUser | null>(null);

	readonly authenticated$ = new BehaviorSubject<boolean>(false);

	private readonly auth = inject(FIREBASE_AUTH);
	private readonly firestore = inject(FIRESTORE);

	constructor() {
		onAuthStateChanged(this.auth, this.onAuthStateChanged.bind(this));
	}

	async onAuthStateChanged(firebaseUser: FirebaseUser | null) {
		if (!firebaseUser) {
			this.reset();
			return;
		}

		this.firebaseUser.set(firebaseUser);

		const authRef = doc(
			collection(
				this.firestore,
				'authUsers',
			) as CollectionReference<AuthUser>,
			firebaseUser.uid,
		);
		const authSnapshot = await getDoc(authRef).catch((error) => {
			console.error('Auth user could not be found', error);
			throw error;
		});

		if (!authSnapshot.exists()) {
			throw new Error('Auth user could not be found');
		}

		const authUserData = authSnapshot.data() as AuthUser;
		this.authUser.set(authUserData);

		this.authenticated$.next(true);
	}

	/**
	 * Signs in with email and password. Resolves once the auth user and role are loaded.
	 * The SuperAdminGuard enforces super role when navigating to protected routes.
	 */
	signInWithEmailAndPassword(email: string, password: string): Promise<void> {
		const resolved$ = new Subject<boolean>();
		return signInWithEmailAndPassword(this.auth, email, password).then(
			() =>
				new Promise<void>((resolve) => {
					this.authenticated$
						.pipe(takeUntil(resolved$))
						.subscribe((value) => {
							if (!value) return;
							resolve();
							resolved$.next(true);
						});
				}),
		);
	}

	readonly signOut = (): Promise<void> =>
		signOut(this.auth).then(() => this.reset());

	private readonly reset = (): void => {
		this.firebaseUser.set(null);
		this.authUser.set(null);
		this.authenticated$.next(false);
	};
}
